import { throttle } from 'lodash-es';
import { useCallback, useRef } from 'react';

import type { CustomGAEvents } from 'utils/googleAnalytics';
import { dispatchGAEvent } from 'utils/googleAnalytics';

import { useOrganisationId } from './context';

/**
 * Hook exposing Google Analytics event dispatcher
 *
 * This hook throttles dispatched events
 */
export const useGoogleAnalyticsDispatcher = () => {
  const throttleEventMapRef = useRef(
    new Map<
      CustomGAEvents,
      ReturnType<typeof throttle<typeof dispatchGAEvent>>
    >()
  );

  const orgId = useOrganisationId();

  const dispatch = useCallback<typeof dispatchGAEvent>(
    (event, payload) => {
      if (!throttleEventMapRef.current.has(event)) {
        throttleEventMapRef.current.set(
          event,
          throttle(dispatchGAEvent, 1000, {
            trailing: false
          })
        );
      }
      const eventDispatcher = throttleEventMapRef.current.get(event)!;

      // Dispatch
      eventDispatcher(event, { org_id: orgId, ...payload });
    },
    [orgId]
  );

  return dispatch;
};
